import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import type { ProgramCertificateMetadata } from '@court-sync/shared';

const styles = StyleSheet.create({
  page: {
    padding: 48,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  border: {
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: '#1e3a5f',
    borderRadius: 8,
    padding: 36,
    minHeight: '100%',
  },
  logo: {
    width: 120,
    height: 48,
    objectFit: 'contain',
    marginBottom: 16,
    alignSelf: 'center',
  },
  academyName: {
    fontSize: 22,
    textAlign: 'center',
    marginBottom: 8,
    color: '#111827',
    fontWeight: 'bold',
  },
  title: {
    fontSize: 28,
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 8,
    color: '#111827',
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    color: '#4b5563',
    marginBottom: 32,
  },
  studentName: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 8,
  },
  programLine: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
    color: '#374151',
  },
  meta: {
    fontSize: 11,
    textAlign: 'center',
    color: '#6b7280',
    marginBottom: 4,
  },
  signatureSection: {
    marginTop: 28,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    alignItems: 'center',
  },
  signatureImage: {
    width: 140,
    height: 48,
    objectFit: 'contain',
    marginBottom: 8,
  },
  signatureName: {
    fontSize: 13,
    textAlign: 'center',
    color: '#111827',
    fontFamily: 'Times-Italic',
  },
  signatureTitle: {
    fontSize: 10,
    textAlign: 'center',
    color: '#6b7280',
    marginTop: 4,
  },
  footer: {
    position: 'absolute',
    bottom: 36,
    left: 48,
    right: 48,
    textAlign: 'center',
    fontSize: 9,
    color: '#9ca3af',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 12,
  },
});

export interface ProgramCertificateDocumentProps {
  meta: ProgramCertificateMetadata;
}

export function ProgramCertificateDocument({ meta }: ProgramCertificateDocumentProps) {
  const accent = meta.academyPrimaryColor && /^#[0-9A-Fa-f]{6}$/.test(meta.academyPrimaryColor)
    ? meta.academyPrimaryColor
    : '#1e3a5f';

  const showSignature =
    Boolean(meta.certificateSignerName) ||
    Boolean(meta.certificateSignerTitle) ||
    Boolean(meta.certificateSignatureImageUrl);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={[styles.border, { borderColor: accent }]}>
          {meta.academyLogoUrl ? (
            <Image src={meta.academyLogoUrl} style={styles.logo} />
          ) : null}
          <Text style={styles.academyName}>{meta.academyName}</Text>
          <Text style={styles.title}>Certificate of Completion</Text>
          <Text style={styles.subtitle}>Program pathway</Text>
          <Text style={[styles.studentName, { color: accent }]}>
            {meta.studentFirstName} {meta.studentLastName}
          </Text>
          <Text style={styles.programLine}>has completed: {meta.programName}</Text>
          <Text style={styles.meta}>Completed: {new Date(meta.completedAt).toLocaleDateString()}</Text>
          {meta.completedByDisplayName ? (
            <Text style={styles.meta}>Recorded by: {meta.completedByDisplayName}</Text>
          ) : null}

          {showSignature ? (
            <View style={styles.signatureSection}>
              {meta.certificateSignatureImageUrl ? (
                <Image src={meta.certificateSignatureImageUrl} style={styles.signatureImage} />
              ) : null}
              {meta.certificateSignerName ? (
                <Text style={styles.signatureName}>{meta.certificateSignerName}</Text>
              ) : null}
              {meta.certificateSignerTitle ? (
                <Text style={styles.signatureTitle}>{meta.certificateSignerTitle}</Text>
              ) : null}
            </View>
          ) : null}
        </View>
        <Text style={styles.footer} fixed>
          Powered by CourtSync — courtsync.io
        </Text>
      </Page>
    </Document>
  );
}
